import type { ModuleId, TrialResult } from '../../core/types';

interface FixedThresholdOptions {
  moduleId: ModuleId;
  metadataKey: string;
  performanceKey?: string;
  direction: 'increasing' | 'decreasing';
  target?: number;
  minimumPerLevel?: number;
}

export function estimateFixedFormThreshold(
  results: readonly TrialResult[],
  options: FixedThresholdOptions,
): number | undefined {
  const target = options.target ?? 0.75;
  const minimumPerLevel = options.minimumPerLevel ?? 2;
  const groups = new Map<number, number[]>();
  for (const result of results) {
    if (!result.valid || result.moduleId !== options.moduleId || result.metadata.assessment !== true) continue;
    const level = result.metadata[options.metadataKey];
    const recordedPerformance = options.performanceKey ? result.metadata[options.performanceKey] : undefined;
    const performance = typeof recordedPerformance === 'number' ? recordedPerformance : result.correct ? 1 : 0;
    if (typeof level !== 'number' || !Number.isFinite(level) || !Number.isFinite(performance)) continue;
    const values = groups.get(level) ?? [];
    values.push(Math.max(0, Math.min(1, performance)));
    groups.set(level, values);
  }
  const levels = [...groups.entries()]
    .filter(([, values]) => values.length >= minimumPerLevel)
    .map(([level, values]) => ({ level, accuracy: values.reduce((sum, value) => sum + value, 0) / values.length }))
    .sort((left, right) => left.level - right.level);
  if (levels.length < 2) return undefined;
  for (let index = 1; index < levels.length; index += 1) {
    const left = levels[index - 1];
    const right = levels[index];
    if (!left || !right) continue;
    const crosses = options.direction === 'increasing'
      ? left.accuracy <= target && right.accuracy >= target
      : left.accuracy >= target && right.accuracy <= target;
    if (!crosses) continue;
    if (left.accuracy === right.accuracy) return (left.level + right.level) / 2;
    const fraction = (target - left.accuracy) / (right.accuracy - left.accuracy);
    return left.level + (right.level - left.level) * fraction;
  }
  return undefined;
}

